import { Navigation } from "@/components/Navigation"
import { Footer } from "@/components/Footer"
import { Button } from "@/components/ui/button"
import {
  ArrowRight,
  Smartphone,
  Eye,
  Mic,
  BrainCircuit,
  Users,
} from "lucide-react"

import { Link } from "react-router-dom"

import { SEO } from "@/components/SEO"
import { PageFAQ } from "@/components/PageFAQ"
import { neuralInterfacesFAQs } from "@/data/faqData"

export default function NeuralInterfaces() {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <SEO
        title="Neural Interfaces | Adaptive Digital Experiences | RSA Tech Group"
        description="Creating immersive, adaptive digital experiences that evolve with user behavior and intent. Beyond the screen to intuitive connection."
        image="https://images.unsplash.com/photo-1617802690992-15d93263d3a9?auto=format&fit=crop&q=80&w=2940"
      />
      <Navigation />

      {/* Hero Section */}
      <div className="relative pt-32 pb-24 bg-primary overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.1)_0%,transparent_70%)] opacity-30 pointer-events-none" />
        <div className="section-container relative z-10 text-center">
          <h1 className="text-4xl md:text-6xl font-black text-white tracking-tight mb-8">
            Neural <span className="text-accent">Interfaces</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-10">
            Creating immersive, adaptive digital experiences that evolve with
            user behavior and intent.
          </p>
        </div>
      </div>

      {/* Content Section */}
      <section className="py-20 bg-gray-50">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
                Beyond the Screen
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                The future of interaction isn't just about clicking buttons;
                it's about intuitive, seamless connection. Neural Interfaces
                bridge the gap between human intent and digital execution using
                advanced UX, voice recognition, and gesture control.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                We design systems that "learn" the user, adapting layout,
                content, and functionality in real-time based on behavioral
                patterns and predictive context.
              </p>

              <div className="grid grid-cols-1 gap-4 mb-8">
                {[
                  { icon: Eye, text: "Eye-Tracking & Attention Optimization" },
                  { icon: Mic, text: "Natural Language Interaction" },
                  { icon: Smartphone, text: "Adaptive Mobile Interfaces" },
                  { icon: Users, text: "Hyper-Personalized User Journeys" },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-center text-primary font-medium p-4 bg-white rounded-lg shadow-sm border border-gray-100"
                  >
                    <div className="p-2 bg-accent/10 rounded-md mr-4">
                      <item.icon className="h-5 w-5 text-accent" />
                    </div>
                    {item.text}
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-accent/10 rounded-lg transform translate-x-4 translate-y-4" />
              <img
                src="https://images.unsplash.com/photo-1617802690992-15d93263d3a9?auto=format&fit=crop&q=80&w=2940"
                alt="Futuristic Interface Touchscreen"
                className="rounded-lg shadow-xl relative z-10 w-full object-cover h-[500px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-white">
        <div className="section-container">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-primary mb-6">
              Key Capabilities
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Adaptive UI",
                desc: "Interfaces that restructure themselves based on user habits and workflow needs.",
                img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71",
              },
              {
                title: "Immersive AR/VR",
                desc: "Blending physical and digital worlds for training, maintenance, and commerce.",
                img: "https://images.unsplash.com/photo-1593508512255-86ab42a8e620",
              },
              {
                title: "Voice & Gesture",
                desc: "Hands-free control systems powered by advanced NLP and computer vision.",
                img: "https://images.unsplash.com/photo-1589254065878-42c9da997008",
              },
            ].map((card, i) => (
              <div
                key={i}
                className="group rounded-xl overflow-hidden shadow-lg border border-gray-100"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={card.img + "?auto=format&fit=crop&q=80&w=800"}
                    alt={card.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-primary mb-3">
                    {card.title}
                  </h3>
                  <p className="text-gray-600">{card.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-16 text-center">
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white rounded-full px-8"
              >
                Transform Your Experience{" "}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PageFAQ
        faqs={neuralInterfacesFAQs}
        title="Neural Interfaces FAQs"
        subtitle="Common questions about adaptive UX and intelligent interactions"
        variant="light"
      />

      <Footer />
    </div>
  )
}
